/**
 * Utilidades de sesión + interceptor global de autenticación.
 *
 * En lugar de tocar cada llamada `fetch` de la aplicación, aquí se envuelve
 * `window.fetch` UNA sola vez: cualquier respuesta 401/403 procedente de
 * nuestra propia API cierra la sesión automáticamente (limpia el token y
 * redirige al login). Replica el comportamiento que ya existía en
 * `Router.js` (`fakeAuth`).
 */

// Estados HTTP que consideramos "sesión no válida".
const AUTH_STATUS = [401, 403];

// Rutas de nuestra API que NO deben provocar cierre de sesión
// (login, obtención de token, etc.), para no romper esos flujos.
const EXCLUDED_PATHS = ["/api/authenticate", "/api/get-token"];

let loggingOut = false;

/**
 * Cierra la sesión: limpia el token del localStorage y redirige.
 * @param {string} redirectTo Ruta a la que redirigir (por defecto la raíz).
 */
export function forceLogout(redirectTo = "/") {
  if (loggingOut) return; // evita redirecciones múltiples
  loggingOut = true;
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("data_token");
    localStorage.removeItem("pk_AES_hash");
  } catch (err) {
    console.log(err);
  }
  window.open(redirectTo, "_self");
}

/**
 * Comprueba si una URL corresponde a una petición a nuestra propia API
 * (mismo origen, prefijo /api) y no está excluida.
 * @param {string} url
 * @returns {boolean}
 */
function isOwnApiRequest(url) {
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return false; // APIs externas (Stripe, etc.)
    if (!u.pathname.startsWith("/api")) return false;
    return !EXCLUDED_PATHS.some((p) => u.pathname.startsWith(p));
  } catch (err) {
    return false;
  }
}

/**
 * Instala (una única vez) un interceptor global sobre `window.fetch`.
 * Cualquier respuesta 401/403 de nuestra API cierra la sesión.
 * La respuesta original se devuelve intacta para no romper a los llamadores.
 */
export function installFetchAuthInterceptor() {
  if (typeof window === "undefined" || window.__fetchAuthInterceptorInstalled) {
    return;
  }
  window.__fetchAuthInterceptorInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      const input = args[0];
      const url = typeof input === "string" ? input : input && input.url;
      const hasToken = !!localStorage.getItem("token");
      if (
        hasToken &&
        AUTH_STATUS.includes(response.status) &&
        isOwnApiRequest(url)
      ) {
        forceLogout();
      }
    } catch (err) {
      console.log(err);
    }
    return response;
  };
}
